import { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Fade, Flex, HStack, Stack, Text, VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { usePushSubscription } from '../push/usePushSubscription';

// Plain fixed-position overlay instead of Chakra Modal — Modal's portal +
// focus-lock + scroll-lock + backdrop-filter blur add ~200–500ms of paint
// work on mobile, which reads as a lag between tapping Generate and seeing
// feedback. This variant mounts instantly.
export function GenerationOverlay({
  isOpen,
  onCancel,
  instrumental,
  isQueued,
}: {
  isOpen: boolean;
  onCancel?: () => void;
  instrumental?: boolean;
  isQueued?: boolean;
}) {
  const { t } = useTranslation();
  const phases = instrumental ? INSTRUMENTAL_PHASES : VOCAL_PHASES;
  const tips = instrumental ? INSTRUMENTAL_TIPS : VOCAL_TIPS;
  // When the job is server-side 'queued' (waiting for a Mureka slot), the
  // phase copy would lie ("Recording the vocals…" while nothing is
  // happening). Show a distinct waiting-in-line message instead until the
  // worker atomically claims it and the status flips to 'processing'.
  const queuedPhase = {
    titleKey: 'genOverlay.q.title',
    titleFallback: 'Waiting for a studio slot…',
    subtitleKey: 'genOverlay.q.subtitle',
    subtitleFallback: 'You’re next in line — this shouldn’t take long.',
  };

  const [phaseIndex, setPhaseIndex] = useState(0);
  const [tipIndex, setTipIndex] = useState(() => Math.floor(Math.random() * tips.length));
  const [tipVisible, setTipVisible] = useState(true);
  const startedAtRef = useRef<number>(0);

  // Reset timers on each open so the overlay always starts at phase 0
  // rather than picking up wherever the previous open left off.
  useEffect(() => {
    if (!isOpen) return;
    startedAtRef.current = Date.now();
    setPhaseIndex(0);
    setTipIndex(Math.floor(Math.random() * tips.length));
    setTipVisible(true);
  }, [isOpen, tips.length]);

  // Advance the phase index by elapsed time. Song generation is longer
  // than image generation (Mureka takes ~30-60s vs pictools ~5-15s), so
  // the thresholds are stretched accordingly.
  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => {
      const elapsed = Date.now() - startedAtRef.current;
      let p = 0;
      for (let i = PHASE_THRESHOLDS.length - 1; i >= 0; i--) {
        if (elapsed >= PHASE_THRESHOLDS[i]) { p = i; break; }
      }
      setPhaseIndex(Math.min(p, phases.length - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [isOpen, phases.length]);

  // Rotate tips every 7s with a fade — long enough to actually read.
  useEffect(() => {
    if (!isOpen) return;
    const id = setInterval(() => {
      setTipVisible(false);
      setTimeout(() => {
        setTipIndex((i) => (i + 1) % tips.length);
        setTipVisible(true);
      }, 400);
    }, 7000);
    return () => clearInterval(id);
  }, [isOpen, tips.length]);

  const phase = isQueued ? queuedPhase : phases[phaseIndex];

  // Lock body scroll while the overlay is up so background touches don't
  // scroll the form underneath. Cheap side-effect, no focus-lock cost.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // Escape-to-cancel, only when cancelable.
  useEffect(() => {
    if (!isOpen || !onCancel) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <Box
      position="fixed"
      inset={0}
      zIndex={2000}
      bg="rgba(10, 6, 20, 0.92)"
      role="dialog"
      aria-modal="true"
      aria-label={t('generator.workingAria', 'Generating your song')}
    >
      <Flex h="100dvh" align="center" justify="center" px={5}>
        <VStack spacing={8} maxW="480px" w="100%" textAlign="center">
            {/* Music-native animated visual: 5 equalizer bars */}
            <Equalizer />

            {/* Phase title + subtitle */}
            <Stack spacing={2}>
              <Text
                fontSize={{ base: 'xl', md: '2xl' }}
                fontWeight={700}
                color="whiteAlpha.900"
                lineHeight="1.25"
                style={{ textWrap: 'balance' as any }}
              >
                {t(phase.titleKey, phase.titleFallback)}
              </Text>
              <Text fontSize="sm" color="whiteAlpha.700">
                {t(phase.subtitleKey, phase.subtitleFallback)}
              </Text>
            </Stack>

            {/* Progress dots (visual indicator, not tied to real backend progress) */}
            <Flex gap="6px">
              {phases.slice(0, -1).map((_, i) => (
                <Box
                  key={i}
                  w="7px"
                  h="7px"
                  borderRadius="full"
                  bg={i <= Math.min(phaseIndex, phases.length - 2) ? 'pink.400' : 'whiteAlpha.300'}
                  transition="background 0.4s ease"
                />
              ))}
            </Flex>

            {/* Shimmer bar */}
            <Box
              w={{ base: '180px', md: '220px' }}
              h="4px"
              borderRadius="full"
              bg="whiteAlpha.200"
              overflow="hidden"
              position="relative"
            >
              <Box
                position="absolute"
                inset={0}
                bgGradient="linear(90deg, transparent 0%, pink.400 40%, purple.400 60%, transparent 100%)"
                backgroundSize="200% 100%"
                sx={{
                  '@keyframes mixgenieShimmer': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' },
                  },
                  animation: 'mixgenieShimmer 2.2s ease-in-out infinite',
                }}
              />
            </Box>

            {/* Rotating tip */}
            <Box
              minH="3rem"
              display="flex"
              alignItems="center"
              justifyContent="center"
              px={4}
              maxW="100%"
            >
              <Fade in={tipVisible} unmountOnExit={false}>
                <Text
                  fontSize={{ base: 'sm', md: 'md' }}
                  color="whiteAlpha.800"
                  fontStyle="italic"
                  style={{ textWrap: 'balance' as any }}
                  maxW="42ch"
                >
                  {t(tips[tipIndex].key, tips[tipIndex].fallback)}
                </Text>
              </Fade>
            </Box>

            <PushNudge />

          {onCancel && (
            <Button
              variant="outline"
              colorScheme="whiteAlpha"
              color="whiteAlpha.900"
              borderColor="whiteAlpha.400"
              onClick={onCancel}
              borderRadius="full"
              px={6}
            >
              {t('generator.cancel', 'Cancel')}
            </Button>
          )}
        </VStack>
      </Flex>
    </Box>
  );
}

// -----------------------------------------------------------------------
// -----------------------------------------------------------------------
// Web-push nudge — captured at the natural moment of "I want to know when
// this is done." Rendered inside the generation overlay so we ask exactly
// when the value is obvious. Hidden after subscribe, after deny, and if
// the user has already answered (localStorage flag survives page reloads).
// The BE fires the completion push in generation-jobs.service.ts's
// executeJob success tail — this hook only handles the FE half.
// -----------------------------------------------------------------------
function PushNudge() {
  const { t } = useTranslation();
  const { supported, permission, subscribed, busy, subscribe } = usePushSubscription();
  const NUDGE_DISMISSED_KEY = 'mixgenie:push-nudge-dismissed';
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(NUDGE_DISMISSED_KEY) === '1'; } catch { return false; }
  });
  // Tracks the just-completed subscribe click within this render session,
  // so we can flash a "we'll ping you ✓" confirmation instead of the
  // component simply disappearing (which would feel like nothing happened).
  const [justAsked, setJustAsked] = useState(false);

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(NUDGE_DISMISSED_KEY, '1'); } catch { /* noop */ }
  };

  // Confirmation banner — appears after the user tapped Yes, permission
  // was granted, and the BE POST succeeded (usePushSubscription flips
  // `subscribed` on success). Auto-dismisses on next generation via the
  // localStorage flag.
  if (justAsked && subscribed) {
    return (
      <Text fontSize="xs" color="green.300">
        {t('genOverlay.push.done', "We'll ping you the second it's ready ✓")}
      </Text>
    );
  }

  // Only show when the browser can push AND the user hasn't decided yet.
  // A 'denied' permission is respected forever — asking again is spammy
  // and blocked by browsers anyway.
  if (!supported || permission !== 'default' || subscribed || dismissed) return null;

  return (
    <HStack spacing={2} fontSize="sm" color="whiteAlpha.900" flexWrap="wrap" justify="center">
      <Text as="span">🔔</Text>
      <Text as="span" color="whiteAlpha.800">
        {t('genOverlay.push.prompt', 'Ping me when it\'s ready?')}
      </Text>
      <Button
        size="xs"
        variant="outline"
        colorScheme="whiteAlpha"
        borderColor="whiteAlpha.400"
        borderRadius="full"
        px={3}
        isLoading={busy}
        onClick={async () => {
          setJustAsked(true);
          await subscribe();
          // Whatever the outcome (granted+sent OR denied), don't nag
          // again on subsequent generations — the localStorage flag
          // makes this decision durable across page reloads.
          dismiss();
        }}
      >
        {t('genOverlay.push.yes', 'Yes')}
      </Button>
      <Button
        size="xs"
        variant="ghost"
        color="whiteAlpha.500"
        borderRadius="full"
        onClick={dismiss}
      >
        {t('genOverlay.push.no', 'No thanks')}
      </Button>
    </HStack>
  );
}

// Equalizer — 5 pink/purple bars that jump on staggered timings. Simpler
// and more on-brand than SVG audio-waveform paths; renders identically on
// mobile + desktop, no external deps.
// -----------------------------------------------------------------------
function Equalizer() {
  return (
    <Flex align="flex-end" justify="center" gap={2} h="110px">
      {[0, 1, 2, 3, 4].map((i) => (
        <Box
          key={i}
          w={{ base: '14px', md: '18px' }}
          bgGradient="linear(to-t, pink.500, purple.500)"
          borderRadius="4px"
          sx={{
            '@keyframes eqBar0': { '0%,100%': { height: '30%' }, '50%': { height: '100%' } },
            '@keyframes eqBar1': { '0%,100%': { height: '65%' }, '50%': { height: '15%' } },
            '@keyframes eqBar2': { '0%,100%': { height: '20%' }, '50%': { height: '85%' } },
            '@keyframes eqBar3': { '0%,100%': { height: '80%' }, '50%': { height: '40%' } },
            '@keyframes eqBar4': { '0%,100%': { height: '45%' }, '50%': { height: '95%' } },
            animation: `eqBar${i} ${0.9 + i * 0.15}s ease-in-out infinite`,
          }}
        />
      ))}
    </Flex>
  );
}

// -----------------------------------------------------------------------
// Copy — phases progress over ~90 s, tips rotate every 7 s. Kept separate
// for vocal vs instrumental so the messages actually match what the model
// is doing.
// -----------------------------------------------------------------------

const PHASE_THRESHOLDS = [0, 8000, 25000, 50000, 80000];

const VOCAL_PHASES = [
  {
    titleKey: 'genOverlay.v.p0.title',
    titleFallback: 'Tuning the instruments…',
    subtitleKey: 'genOverlay.v.p0.subtitle',
    subtitleFallback: 'Setting up the studio',
  },
  {
    titleKey: 'genOverlay.v.p1.title',
    titleFallback: 'Laying down the beat…',
    subtitleKey: 'genOverlay.v.p1.subtitle',
    subtitleFallback: 'Building the rhythm section',
  },
  {
    titleKey: 'genOverlay.v.p2.title',
    titleFallback: 'Recording the vocals…',
    subtitleKey: 'genOverlay.v.p2.subtitle',
    subtitleFallback: 'Coaching the AI on delivery',
  },
  {
    titleKey: 'genOverlay.v.p3.title',
    titleFallback: 'Mixing and mastering…',
    subtitleKey: 'genOverlay.v.p3.subtitle',
    subtitleFallback: 'Getting the levels just right',
  },
  {
    titleKey: 'genOverlay.v.p4.title',
    titleFallback: 'Almost there…',
    subtitleKey: 'genOverlay.v.p4.subtitle',
    subtitleFallback: 'Sometimes the encore takes a moment',
  },
];

const INSTRUMENTAL_PHASES = [
  {
    titleKey: 'genOverlay.i.p0.title',
    titleFallback: 'Tuning the instruments…',
    subtitleKey: 'genOverlay.i.p0.subtitle',
    subtitleFallback: 'Setting up the studio',
  },
  {
    titleKey: 'genOverlay.i.p1.title',
    titleFallback: 'Sketching the arrangement…',
    subtitleKey: 'genOverlay.i.p1.subtitle',
    subtitleFallback: 'Deciding who plays what',
  },
  {
    titleKey: 'genOverlay.i.p2.title',
    titleFallback: 'Recording the sections…',
    subtitleKey: 'genOverlay.i.p2.subtitle',
    subtitleFallback: 'Layering the parts',
  },
  {
    titleKey: 'genOverlay.i.p3.title',
    titleFallback: 'Mixing and mastering…',
    subtitleKey: 'genOverlay.i.p3.subtitle',
    subtitleFallback: 'Getting the levels just right',
  },
  {
    titleKey: 'genOverlay.i.p4.title',
    titleFallback: 'Almost there…',
    subtitleKey: 'genOverlay.i.p4.subtitle',
    subtitleFallback: 'The instrumental bridge is worth the wait',
  },
];

const VOCAL_TIPS = [
  { key: 'genOverlay.tip.v0', fallback: 'Convincing the AI it can carry a tune…' },
  { key: 'genOverlay.tip.v1', fallback: 'Warming up the vocal cords…' },
  { key: 'genOverlay.tip.v2', fallback: 'Debating whether the chorus needs another lift…' },
  { key: 'genOverlay.tip.v3', fallback: 'Adding just the right amount of reverb…' },
  { key: 'genOverlay.tip.v4', fallback: 'Bribing the drummer with a snack…' },
  { key: 'genOverlay.tip.v5', fallback: 'Whispering the lyrics to the machine…' },
  { key: 'genOverlay.tip.v6', fallback: 'Finding the perfect key change…' },
  { key: 'genOverlay.tip.v7', fallback: 'Auditioning drum patterns…' },
  { key: 'genOverlay.tip.v8', fallback: 'Turning it up to eleven…' },
  { key: 'genOverlay.tip.v9', fallback: 'Waiting for the muse to show up…' },
  { key: 'genOverlay.tip.v10', fallback: 'Sampling ideas from the multiverse…' },
  { key: 'genOverlay.tip.v11', fallback: 'Coaxing the strings into harmony…' },
];

const INSTRUMENTAL_TIPS = [
  { key: 'genOverlay.tip.i0', fallback: 'Tuning the guitars…' },
  { key: 'genOverlay.tip.i1', fallback: 'Bribing the drummer with a snack…' },
  { key: 'genOverlay.tip.i2', fallback: 'Debating between B♭ and A♯…' },
  { key: 'genOverlay.tip.i3', fallback: 'Adding just the right amount of reverb…' },
  { key: 'genOverlay.tip.i4', fallback: 'Finding the perfect key change…' },
  { key: 'genOverlay.tip.i5', fallback: 'Making sure the bass drops on cue…' },
  { key: 'genOverlay.tip.i6', fallback: 'Coaxing the strings into harmony…' },
  { key: 'genOverlay.tip.i7', fallback: 'Feeding the metronome…' },
  { key: 'genOverlay.tip.i8', fallback: 'Turning it up to eleven…' },
  { key: 'genOverlay.tip.i9', fallback: 'Sampling ideas from the multiverse…' },
  { key: 'genOverlay.tip.i10', fallback: 'Handpicking notes like a fussy chef…' },
  { key: 'genOverlay.tip.i11', fallback: 'Auditioning drum patterns…' },
];
